import { Modal, notification } from "antd";
import { get } from "lodash";
import moment from "moment";
import { MdCloudDownload, MdSend } from "react-icons/md";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { useScreenActionPermissions } from "shared/hooks/usePermissions";
import format from "string-template";
import {
  SEND_ADDITIONAL_DOCUMENTS,
  SEND_TO_MANAGEMENT,
} from "utils/api/graphql/mutations/subscription";
import {
  GET_MANAGEMENT_ATTACHMENTS,
  SUBSCRIPTION,
} from "utils/api/graphql/queries/subscription";
import {
  ACTIONS,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  MODULES,
  SEND_TO_MANAGEMENT_SEND_STATUSES,
  SUBMODULES,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";

const Signed = ({ translation, subscription, percentages }) => {
  const externalManagement = get(subscription, "externalManagement") || {};
  const lastSentToManagement = get(externalManagement, "lastSentToManagement");
  const lastSendStatus = get(externalManagement, "lastSendStatus");
  const lastSendError = get(externalManagement, "lastSendError");
  const lastSendAttemptAt = get(externalManagement, "lastSendAttemptAt");
  const hasNeverBeenSent = !lastSentToManagement && !lastSendAttemptAt;
  const isLastSendFailure =
    lastSendStatus === SEND_TO_MANAGEMENT_SEND_STATUSES.FAILURE;
  const isLastSendSuccess =
    lastSendStatus === SEND_TO_MANAGEMENT_SEND_STATUSES.SUCCESS;
  const isLastSendPending =
    lastSendStatus === SEND_TO_MANAGEMENT_SEND_STATUSES.PENDING;
  const showResendButton = !isLastSendSuccess && !isLastSendPending;
  const showTransmitComplementaryDocuments =
    isLastSendSuccess || Boolean(lastSentToManagement);

  // While a send is dispatched asynchronously (slow external flows such as
  // Alptis), poll the subscription so the UI reflects the final SUCCESS/FAILURE
  // once the background task resolves.
  useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscription.id } },
    skip: !isLastSendPending,
    pollInterval: 5000,
    fetchPolicy: "network-only",
  });

  const actions = useScreenActionPermissions({
    module: MODULES.SUBSCRIPTION,
    submodule: SUBMODULES.BROKER_DETAIL,
  });
  // const canSendToManagement = get(actions, ACTIONS.SEND_TO_MANAGEMENT);
  const canSendToManagement = true;

  const [sendComplementaryDocuments, { loading: transmitLoading }] =
    useMutation(SEND_ADDITIONAL_DOCUMENTS, {
      refetchQueries: [
        {
          query: SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id: subscription.id } },
        },
      ],
    });

  const [sendToManagement, { loading }] = useMutation(SEND_TO_MANAGEMENT, {
    refetchQueries: [
      {
        query: SUBSCRIPTION,
        awaitRefetchQueries: true,
        variables: { where: { id: subscription.id } },
      },
    ],
    onCompleted: (data) => {
      const sentStatus = get(
        data,
        "sendToManagement.externalManagement.lastSendStatus",
      );
      if (sentStatus === SEND_TO_MANAGEMENT_SEND_STATUSES.PENDING) {
        notification.info({
          message: translation.SEND_TO_MANAGEMENT_PENDING_TOAST,
          duration: 5,
        });
        return;
      }
      notification.success({
        message: translation.SEND_TO_MANAGEMENT_SUCCESS,
        duration: 5,
      });
    },
    onError: () => {
      notification.error({
        message: translation.SEND_TO_MANAGEMENT_FAILED_TOAST,
        duration: 5,
      });
    },
  });

  const [getManagementAttachments, { loading: downloading }] = useLazyQuery(
    GET_MANAGEMENT_ATTACHMENTS,
    {
      onCompleted: ({ getManagementAttachments: result }) => {
        if (result) {
          downloadDocument(result);
        }
      },
      fetchPolicy: "no-cache",
    },
  );

  const handleTransmitComplementaryDocuments = () => {
    sendComplementaryDocuments({
      variables: { where: { id: subscription.id } },
      onCompleted: () => {
        notification.success({
          message: translation.TRANSMIT_COMPLEMENTARY_DOCUMENTS_SUCCESS,
          duration: 5,
        });
      },
      onError: (error) => {
        const graphQlMessage = get(error, "graphQLErrors.0.message");
        notification.error({
          message:
            (graphQlMessage && translation[graphQlMessage]) ||
            translation.TRANSMIT_COMPLEMENTARY_DOCUMENTS_FAILED,
          duration: 5,
        });
      },
    });
  };

  const triggerSendToManagement = () => {
    sendToManagement({
      variables: {
        where: { id: subscription.id },
      },
    });
  };

  const handleSendToManagement = () => {
    if (hasNeverBeenSent) {
      triggerSendToManagement();
      return;
    }
    Modal.confirm({
      title: translation.RESEND_CONFIRM_TITLE,
      content: translation.RESEND_CONFIRM_CONTENT,
      okText: translation.RESEND_CONFIRM_OK,
      cancelText: translation.RESEND_CONFIRM_CANCEL,
      onOk: triggerSendToManagement,
    });
  };

  const handleDownloadAttachments = () => {
    getManagementAttachments({
      variables: {
        where: { id: subscription.id },
      },
    });
  };

  const transmitLabel = get(translation, "PROOF.ACTION.SEND_DOCUMENTS");

  const renderManagementAlert = () => {
    if (isLastSendPending) {
      return (
        <Alert
          showIcon
          type="info"
          message={translation.SEND_TO_MANAGEMENT_PENDING_INFO}
        />
      );
    }
    if (isLastSendSuccess && lastSentToManagement) {
      return (
        <Alert
          showIcon
          type="success"
          message={format(translation.SEND_TO_MANAGEMENT_SUCCESS_AT, {
            date: moment(lastSentToManagement).format(DATE_TIME_FORMAT),
          })}
        />
      );
    }
    if (isLastSendFailure) {
      return (
        <Alert
          showIcon
          type="error"
          message={format(translation.SEND_TO_MANAGEMENT_FAILED_AT, {
            date: moment(lastSendAttemptAt || lastSentToManagement).format(
              DATE_TIME_FORMAT,
            ),
            error: lastSendError || "",
          })}
        />
      );
    }
    if (lastSentToManagement) {
      return (
        <Alert
          showIcon
          type="info"
          message={format(translation.SEND_TO_MANAGEMENT_ALREADY_SENT, {
            date: moment(lastSentToManagement).format(DATE_TIME_FORMAT),
          })}
        />
      );
    }
    return null;
  };

  const sendButtonLabel = hasNeverBeenSent
    ? translation.SEND_TO_MANAGEMENT
    : translation.RESEND_TO_MANAGEMENT;

  return (
    <div className="grid--1">
      <div className="signature_signed">
        <span>{`${translation.E_SIGNATURE_SIGNED} ${moment(
          subscription.signedDate,
        ).format(DATE_FORMAT)}`}</span>
        <br />
        <br />
      </div>
      {renderManagementAlert()}
      {!canSendToManagement && (
        <Alert
          showIcon
          type="warning"
          message={translation.SEND_TO_MANAGEMENT_FORBIDDEN}
        />
      )}
      <div className="subscription-signed-actions grid--2">
        {showTransmitComplementaryDocuments && (
          <Button
            type="primary--outlined"
            onClick={handleTransmitComplementaryDocuments}
            loading={transmitLoading}
            disabled={
              get(percentages, "proofPercentage") < 100 ||
              transmitLoading ||
              !canSendToManagement
            }
            block
          >
            <span className="subscription-signed-action__label">
              {transmitLabel}
            </span>
            <MdSend size={16} />
          </Button>
        )}
        {showResendButton && (
          <Button
            type="primary--outlined"
            onClick={handleSendToManagement}
            loading={loading}
            disabled={
              loading ||
              !canSendToManagement ||
              get(percentages, "additionalInfoPercentage") < 100
            }
            block
          >
            <span className="subscription-signed-action__label">
              {sendButtonLabel}
            </span>
            <MdSend size={16} />
          </Button>
        )}
        <Button
          type="primary"
          onClick={handleDownloadAttachments}
          loading={downloading}
          disabled={downloading || !canSendToManagement}
          block
        >
          <span className="subscription-signed-action__label">
            {translation.DOWNLOAD_MANAGEMENT_ATTACHMENTS}
          </span>
          <MdCloudDownload size={16} />
        </Button>
      </div>
    </div>
  );
};

export default Signed;
